import React, { useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Header } from './Header';
import { Footer } from './Footer';
import { SEO } from './SEO';
import { 
  Globe, Users, Award, TrendingUp, 
  Shield, Zap, DollarSign, Clock, 
  Server, MapPin, Mail, Building2,
  CheckCircle, Target, Lightbulb, Info, Send
} from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';

export const AboutPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getLocalizedPath = (path) => {
    if (language === 'ru') {
      return '/ru' + path;
    }
    return path;
  };

  const handlePricingClick = () => {
    navigate(getLocalizedPath('/'));
    setTimeout(() => {
      const element = document.querySelector('#pricing');
      if (element) {
        const targetPosition = element.offsetTop + 200;
        window.scrollTo({ top: targetPosition, behavior: 'smooth' });
      }
    }, 100);
  };

  const stats = [
    { icon: Clock, value: "15+", label: t('about.stats.years') },
    { icon: Users, value: "12,000+", label: t('about.stats.clients') },
    { icon: MapPin, value: "12", label: t('about.stats.locations') },
    { icon: Shield, value: "99.9%", label: t('about.stats.uptime') }
  ];

  const values = [
    {
      icon: DollarSign,
      title: t('about.values.pricing.title'),
      description: t('about.values.pricing.desc')
    },
    {
      icon: Award,
      title: t('about.values.experience.title'),
      description: t('about.values.experience.desc')
    },
    {
      icon: Zap,
      title: t('about.values.performance.title'),
      description: t('about.values.performance.desc')
    },
    {
      icon: Users,
      title: t('about.values.support.title'),
      description: t('about.values.support.desc')
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO page="about" />
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-950 via-blue-800 to-blue-950 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-xl mb-6">
              <Building2 className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {t('about.hero.title')}
            </h1>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto">
              {t('about.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                {t('about.story.title')}
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>{t('about.story.paragraph1')}</p>
                <p>{t('about.story.paragraph2')}</p>
                <p>{t('about.story.paragraph3')}</p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden">
              <img src="/images/locations/unsplash_1521737711867-e3b97375f902.jpg" 
                alt="Successful Team" loading="lazy"
                className="w-full h-96 object-cover"
 loading="lazy"/>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-xl mb-4">
                  <stat.icon className="w-8 h-8 text-blue-600" />
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Info Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-8">
                {t('about.company.title')}
              </h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{t('about.company.legalName')}</h4>
                    <p className="text-gray-600">NetGrid Host Ltd</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{t('about.company.address')}</h4>
                    <p className="text-gray-600">Unit A, 82 James Carter Road<br/>Mildenhall<br/>IP28 7DE<br/>United Kingdom</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">{t('about.company.email')}</h4>
                    <a href="mailto:about@netgrid.host" className="text-blue-600 hover:text-blue-700">
                      about@netgrid.host
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden">
              <img src="/images/locations/unsplash_1486406146926-c627a92ad1ab.jpg" 
                alt="Office Building" loading="lazy"
                className="w-full h-full object-cover"
 loading="lazy"/>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Goals Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('about.vision.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('about.vision.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="rounded-2xl overflow-hidden">
              <img src="/images/locations/unsplash_1522071820081-009f0129c71c.jpg" 
                alt="Team Collaboration" loading="lazy"
                className="w-full h-96 object-cover"
 loading="lazy"/>
            </div>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Globe className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {t('about.vision.global.title')}
                  </h3>
                  <p className="text-gray-600">
                    {t('about.vision.global.desc')}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 text-blue-700" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {t('about.vision.accessibility.title')}
                  </h3>
                  <p className="text-gray-600">
                    {t('about.vision.accessibility.desc')}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Lightbulb className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {t('about.vision.innovation.title')}
                  </h3>
                  <p className="text-gray-600">
                    {t('about.vision.innovation.desc')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('about.whyUs.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('about.whyUs.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border-2 border-gray-200">
                <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <value.icon className="w-7 h-7 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Media Updates Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 sm:p-12 border-2 border-blue-200 shadow-lg">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">
              {t('about.social.title')}
            </h2>
            <p className="text-lg font-semibold text-blue-900 mb-8 text-center">
              {t('about.social.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://t.me/NetGridHost" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300 text-lg"
              >
                <Send className="w-5 h-5" />
                Join Telegram
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            {t('about.cta.title')}
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            {t('about.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={handlePricingClick}
              className="bg-white text-blue-700 hover:bg-blue-50 font-semibold px-8 py-6 text-lg"
            >
              {t('about.cta.pricing')}
            </Button>
            <Button 
              onClick={() => navigate(getLocalizedPath('/support'))}
              className="bg-blue-800 text-white hover:bg-blue-900 font-semibold px-8 py-6 text-lg"
            >
              {t('about.cta.contact')}
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};


